import {
  CheckOutlined,
  DeleteOutlined,
  FileTextOutlined,
  PlusOutlined,
  ShoppingCartOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Checkbox, message, Spin } from 'antd';
import Form from 'antd/lib/form';
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import orderApi from '../../apis/order';
import shippoApi from '../../apis/shippo';
import { getActiveStoreId } from '../../constants/store';
import {
  IDesign,
  IOrder,
  IOrderItem,
  ISize,
  OrderType,
  ProductOrderTypeMap,
  ProductType,
  ProductVariantLabelMap,
  RequestStatus,
  getVariantSelectOptions,
  getProductVariantsByProductType,
  productHasVariantsByProductType,
  requiresTrackingReferenceByProductType,
  resolveUnitPrice,
  supportsHardTubeShipping,
} from '../../interfaces/Order';
import { ButtonGroupContainer, PageContainer } from '../../styles/Global';
import CustomForm from '../Form/Form';
import CustomInput from '../Form/FormItem/Input/Input';
import SearchDesignModal from '../Modal/SearchDesignModal';
import PageHeader from '../PageHeader/PageHeader';
import {
  AddItemButton,
  CheckoutDraftContainer,
  CheckoutDraftContent,
  CheckoutDraftRow,
  CreateOrderItemWrapper,
  DeleteIcon,
  DeleteIconContainer,
  ImageContainer,
  OrderContentWrapper,
  OrderFormSection,
  OrderInfoField,
  OrderInformationSection,
  OrderInfoRow,
  OrderItemsContainer,
  OrderItemWrapper,
  PhoneCaseEditorContainer,
  ShippingInfoField,
  ShippingInformationSection,
  ShippingInfoRow,
  SpinContainer,
  StyledInputNumber,
  StyledSelect,
} from './Order.styled';

import {
  Country,
  CountryShippingFeeMap,
  OrderDetailTitleMap,
  OrderStatus,
  NeedsShippoPurchaseSentinels,
  RequestAdminPurchaseShipment,
  ShipWithHardTubeExtraCharge,
  isNeedsShippoPurchase,
  isShippoEligibleOrderType,
} from '../../constants/order';
import { ButtonType } from '../../constants/style';
import { RootState } from '../../reducers';
import { IAuthState } from '../../reducers/auth';
import { Role } from '../../role/role';
import Button from '../Button/Button';
import ConfirmModal from '../Modal/ConfirmModal';
import RequestResendModal from '../Modal/RequestResendModal';
import PhoneCaseEditor from '../PhoneCaseEditor/PhoneCaseEditor';
import CustomTextArea from '../TextArea/TextArea';

export interface SizeForm extends ISize {
  publishedDateISO?: Date;
  startedAtISO?: Date;
  endedAtISO?: Date;
  bannerImage?: any;
  horizontalCoverImage?: any;
  horizontalCoverV2Image?: any;
  playlistLinksItems?: {
    playlistLinks: string;
  }[];
}

const OrderDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const [order, setOrder] = useState<IOrder>({
    orderId: '',
    address: '',
    country: Country.US,
    designs: [],
    createShippingLabel: false,
    shipWithHardTube: false,
  });
  const [sizes, setSizes] = useState<ISize[]>([]);
  const [visibleModal, setVisibleModal] = useState(false);
  const [listDesign, setListDesign] = useState<string[]>([]);
  const [currentDesignIdx, setCurrentDesignIdx] = useState(0);
  const [country, setCountry] = useState(Country.US);
  const [status, setStatus] = useState(OrderStatus.PENDING);
  const [sellerOrderId, setSellerOrderId] = useState<string>();
  const [showPhoneDesignCanvas, setShowPhoneDesignCanvas] = useState(false);
  const [phoneModel, setPhoneModel] = useState<string>();
  const [totalPhoneCaseCount] = useState(1);
  const { currentUser } = useSelector(
    (state: RootState) => state.auth as IAuthState,
  );
  const isAdmin = currentUser?.role === Role.ADMIN;
  const [total, setTotal] = useState(0);
  const [visibleRequestModal, setVisibleRequestModal] = useState(false);
  const [confirmationModalVisible, setConfirmationModalVisible] =
    useState(false);
  const [hasOwnTrackingReference, setHasOwnTrackingReference] = useState(false);
  const [shouldLockTotalCost, setShouldLockTotalCost] = useState(false);
  const [disableTrackingReference, setDisableTrackingReference] =
    useState(false);
  const params = useParams();
  const { productType } = params as { productType: ProductType };
  const isPhoneCaseProduct = productType === ProductType.iphone;
  const isUVDTFProduct = productType === ProductType.uvdtf;
  const isStickerProduct = productType === ProductType.sticker;
  const isMagnetStickerProduct = productType === ProductType.magnet_sticker;
  // Card Skin shares the sticker flow (generic create endpoint, EasyPost
  // label) but is NOT Shippo-eligible — see ShippoEligibleOrderTypes.
  const isCardSkinProduct = productType === ProductType.card_skin;
  const isStickerOrUVDTFProduct =
    isStickerProduct ||
    isUVDTFProduct ||
    isMagnetStickerProduct ||
    isCardSkinProduct;
  // Poster-like products share the dedicated create endpoint and order flow.
  const showHardTubeOption = supportsHardTubeShipping(productType);
  // Variant-bearing products require a `variant` per design (badge_reel:
  // belt/swivel clip; glass_suncatcher: round/oval/heart).
  const hasVariants = productHasVariantsByProductType(productType);
  const variantOptions = useMemo(
    () =>
      getProductVariantsByProductType(productType).map((v) => ({
        label: ProductVariantLabelMap[v],
        value: v,
      })),
    [productType],
  );
  const getRowVariantOptions = useCallback(
    (sizeId?: string) => {
      const size = sizes.find((s) => s.id === sizeId);
      return getVariantSelectOptions(size, productType);
    },
    [sizes, productType],
  );
  // Glass Suncatcher requires a non-empty tracking reference on
  // create + update; backend will 400 otherwise.
  const trackingRequired = requiresTrackingReferenceByProductType(productType);
  const onlySizeId =
    !isPhoneCaseProduct && sizes.length === 1 && !sizes[0].isOutOfStock
      ? sizes[0].id
      : undefined;
  const isFormDisabled = useMemo(() => {
    return !isAdmin && !!id && status !== OrderStatus.PENDING;
  }, [isAdmin, id, status]);

  /**
   * Seller-facing country shipping fee. Matches backend CountryExtraCostMap:
   * waived only for seller-supplied tracking (not EasyPost / locked totals).
   */
  const getSellerShippingFee = useCallback(
    (countryValue?: Country, hasTracking?: boolean, lockTotal?: boolean) => {
      if (isPhoneCaseProduct) {
        return 0;
      }
      // Shippo purchases replace the country fee entirely — the label fee is
      // shown on its own row and totalCost = items + labelFee.
      if (order?.labelFee != null) {
        return 0;
      }
      const tracking = hasTracking ?? hasOwnTrackingReference;
      const locked = lockTotal ?? shouldLockTotalCost;
      // EasyPost labels set shouldLockTotalCost — fee still applies.
      if (tracking && !locked) {
        return 0;
      }
      const country = countryValue || form.getFieldValue('country') || Country.US;
      return CountryShippingFeeMap[country as Country] ?? CountryShippingFeeMap[Country.US];
    },
    [
      isPhoneCaseProduct,
      order?.labelFee,
      hasOwnTrackingReference,
      shouldLockTotalCost,
      form,
    ],
  );

  const showsSellerShippingFee = useCallback(() => {
    return !isPhoneCaseProduct && getSellerShippingFee() > 0;
  }, [isPhoneCaseProduct, getSellerShippingFee]);

  const calculateTotal = useCallback(() => {
    const shippingFee = getSellerShippingFee();

    const designs: IOrderItem[] = form.getFieldValue('designs');

    if (!designs) {
      return shippingFee + (order?.labelFee || 0);
    }

    let total = designs.reduce(
      (accumulator, currentValue) =>
        accumulator + (currentValue?.itemCost || 0),
      shippingFee,
    );

    if (isPhoneCaseProduct) {
      const additionalFee = designs.reduce(
        (accumulator, design) => accumulator + design.quantity,
        -1,
      );

      total += additionalFee;
    }

    // Add hard tube charge for poster-like orders (Poster, BadgeReel) if checked
    if (showHardTubeOption && form.getFieldValue('shipWithHardTube')) {
      total += ShipWithHardTubeExtraCharge;
    }

    // Shippo label fee baked into total after admin purchase
    if (order?.labelFee) {
      total += order.labelFee;
    }

    return total;
  }, [
    isPhoneCaseProduct,
    getSellerShippingFee,
    showHardTubeOption,
    form,
    order?.labelFee,
  ]);

  const fetchOrder = useCallback(
    async (id: string) => {
      if (!id) return;

      try {
        setLoading(true);
        const result = await orderApi.getOrderDetail(id);

        const {
          orderId,
          address,
          trackingReference,
          country,
          orderDesigns,
          totalCost,
          status,
          resendRequest,
          shippingCarrier,
          shippingService,
          shippingCost,
          estimatedDeliveryDate,
          shipmentStatus,
          shipmentId,
          trackingUrl,
          shippingLabelUrl,
          labelFee,
          labelProvider,
          shipWithHardTube,
          notes,
          shouldLockTotalCost: resultShouldLockTotalCost,
        } = result;
        let newListDesign: string[] = [];
        const tmp = orderDesigns?.map((d) => {
          if (d?.design?.designUrl) {
            newListDesign?.push(
              d.design.thumbnailUrl ??
                d.design.originalFileUrl ??
                d.design.designUrl,
            );
          }
          return {
            itemCost: d?.itemCost || 0,
            sizeId: d?.size?.id,
            designId: d?.design?.id,
            quantity: d?.quantity,
            id: d?.id,
            product: d?.size?.product,
            // Backend returns `null` for products without variants — coerce to
            // undefined so the form Select doesn't render an empty option.
            variant: d?.variant ?? undefined,
          } as IOrderItem;
        });

        // EasyPost purchases lock the country fee into totalCost. Fall back to
        // shipment/label presence for older orders missing the flag.
        const lockedTotal = !!(
          resultShouldLockTotalCost ||
          shipmentId ||
          shippingLabelUrl
        );

        setListDesign(newListDesign);
        setOrder({
          orderId,
          address,
          trackingReference,
          country,
          designs: tmp,
          totalCost,
          resendRequest,
          status,
          id,
          shippingCarrier,
          shippingService,
          shippingCost,
          estimatedDeliveryDate,
          shipmentStatus,
          shipmentId,
          trackingUrl,
          shippingLabelUrl,
          labelFee,
          labelProvider,
          shipWithHardTube,
          notes,
          shouldLockTotalCost: lockedTotal,
        });
        setSellerOrderId(orderId);
        setCountry(country as Country);
        setStatus(status as OrderStatus);
        form.setFieldsValue({
          orderId,
          address,
          trackingReference,
          country,
          designs: tmp,
          status,
          shipWithHardTube,
          notes,
        });
        setHasOwnTrackingReference(!!trackingReference);
        setShouldLockTotalCost(lockedTotal);
        const tmpTotal = calculateTotal();
        setTotal(totalCost ?? tmpTotal);
      } catch (error) {
        message.error('Failed to load order details. Please try again.');
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [calculateTotal, form],
  );

  const fetchSize = useCallback(async () => {
    try {
      const result = await orderApi.getSizes({
        page: 1,
        perPage: 200,
        all: true,
        product: productType,
      });
      setSizes(result.items.filter((size) => size.product === productType));
    } catch (error) {
      console.error(error);
    }
  }, [productType]);

  const updateOrder = async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      const values = form.getFieldsValue(true);
      const {
        orderId,
        address,
        trackingReference,
        country,
        designs,
        status,
        shipWithHardTube,
        notes,
      } = values;

      const payload: IOrder = {
        orderId,
        address,
        trackingReference,
        country,
        designs,
        status,
        notes,
      };

      // Include shipWithHardTube only for products that offer the toggle
      if (showHardTubeOption) {
        payload.shipWithHardTube = shipWithHardTube;
      }

      // Only include createShippingLabel for sticker products
      if (isStickerOrUVDTFProduct && !trackingReference) {
        payload.createShippingLabel = true;
      }

      // Same variant + tracking guardrails as createOrder so the FE never
      // sends a payload the backend will 400.
      if (trackingRequired && !trackingReference?.trim()) {
        message.error(
          `${OrderDetailTitleMap[productType]} orders require a tracking reference.`,
        );
        return;
      }
      if (hasVariants) {
        const validVariants = new Set(variantOptions.map((o) => o.value));
        const missingIdx = payload.designs?.findIndex(
          (d) => !d.variant || !validVariants.has(d.variant),
        );
        if (missingIdx !== undefined && missingIdx >= 0) {
          message.error(
            `Please pick a variant for item #${missingIdx + 1}.`,
          );
          return;
        }
      } else if (payload.designs) {
        payload.designs = payload.designs.map(({ variant: _v, ...rest }) =>
          rest,
        );
      }

      const updatedOrder = await orderApi.updateOrder(id, payload);
      setStatus(status);
      setCountry(country);

      // Refresh order data without navigating away
      if (updatedOrder) {
        // Update state with the returned data
        setOrder({
          ...order,
          ...updatedOrder,
        });
        message.success('Order updated successfully!');
      } else {
        // Fallback to fetching the full order again
        fetchOrder(id);
        message.success('Order updated successfully!');
      }
    } catch (e: any) {
      console.error(e);
      message.error(e?.message || 'Update order failed');
    } finally {
      setLoading(false);
    }
  };

  const deleteOrder = async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      await orderApi.deleteOrder([id]);
      message.success('Delete order success!');
      navigate(-1);
    } catch (e) {
      message.error('Delete order fail!');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async () => {
    try {
      setLoading(true);
      const values = form.getFieldsValue(true);
      const { trackingReference, requestAdminPurchaseShipment } = values;

      const activeStoreId = getActiveStoreId();
      if (!activeStoreId) {
        message.error(
          'Please select a store (Chọn Store) in the header before creating an order.'
        );
        return;
      }

      const payload: IOrder = { ...values, storeId: activeStoreId };

      // Include shipWithHardTube only for products that offer the toggle
      if (showHardTubeOption) {
        payload.shipWithHardTube = values.shipWithHardTube;
      }

      // Remove createShippingLabel for non-sticker products
      if (!isStickerOrUVDTFProduct) {
        delete payload.createShippingLabel;
      } else if (!trackingReference && !requestAdminPurchaseShipment) {
        payload.createShippingLabel = true;
      }

      const error = payload.designs?.some((d) => !d.designId || !d.sizeId);

      if (error) {
        message.error('Please input all required field');
        return;
      }

      // Glass Suncatcher (and any future product in
      // ORDER_TYPES_REQUIRING_TRACKING) requires a non-empty tracking reference.
      if (trackingRequired && !trackingReference?.trim()) {
        message.error(
          `${OrderDetailTitleMap[productType]} orders require a tracking reference.`,
        );
        return;
      }

      // Variant-bearing products (badge_reel, glass_suncatcher) require every
      // design to carry a valid variant.
      if (hasVariants) {
        const validVariants = new Set(variantOptions.map((o) => o.value));
        const missingIdx = payload.designs?.findIndex(
          (d) => !d.variant || !validVariants.has(d.variant),
        );
        if (missingIdx !== undefined && missingIdx >= 0) {
          message.error(
            `Please pick a variant for item #${missingIdx + 1}.`,
          );
          return;
        }
      } else if (payload.designs) {
        // Strip stray variants for products that don't support them — the
        // backend rejects the request otherwise.
        payload.designs = payload.designs.map(({ variant: _v, ...rest }) =>
          rest,
        );
      }

      let createdOrder;
      switch (productType) {
        case ProductType.poster:
          createdOrder = await orderApi.createPosterOrder(payload);
          break;
        case ProductType.badge_reel:
          createdOrder = await orderApi.createBadgeReelOrder(payload);
          break;
        case ProductType.glass_suncatcher:
          createdOrder = await orderApi.createGlassSuncatcherOrder(payload);
          break;
        case ProductType.squishy_belly:
          createdOrder = await orderApi.createSquishyBellyOrder(payload);
          break;
        case ProductType.ornament:
          createdOrder = await orderApi.createOrnamentOrder(payload);
          break;
        case ProductType.iphone:
          createdOrder = await orderApi.createPhoneCaseOrder(payload);
          break;
        case ProductType.sticker:
        case ProductType.uvdtf:
        case ProductType.magnet_sticker:
        case ProductType.card_skin:
        default:
          createdOrder = await orderApi.createOrder({
            ...payload,
            type: ProductOrderTypeMap[productType] as OrderType,
          });
          break;
      }

      if (!!id) {
        message.success('Order created successfully!');
        // Navigate to edit page for the newly created order
        navigate(`/order/${createdOrder.id}/edit/${productType}`, {
          replace: true,
        });
      } else {
        message.success('Order created successfully!');
        navigate(-1);
      }
    } catch (e: any) {
      console.error(e);
      message.error(e?.message || 'Create order failed');
    } finally {
      setLoading(false);
    }
  };

  const handleTotal = useCallback(
    (_, values) => {
      const orderItems = [...values.designs];
      if (orderItems.length === 0) {
        setTotal(0);
        return;
      }

      values?.designs.forEach((fieldGroup: IOrderItem, index) => {
        if (fieldGroup && fieldGroup?.quantity && fieldGroup?.sizeId) {
          const idx = sizes?.findIndex((s) => s?.id === fieldGroup?.sizeId);
          if (idx < 0) {
            return;
          }
          const unitPrice =
            resolveUnitPrice(sizes[idx], fieldGroup.variant) ??
            sizes[idx]?.price ??
            0;
          fieldGroup.itemCost = fieldGroup?.quantity * unitPrice;
          orderItems.splice(index, 1, fieldGroup);
          form.setFieldsValue({ designs: orderItems });
          const tmpTotal = calculateTotal();
          setTotal(tmpTotal);
        }
      });
    },
    [sizes, form, calculateTotal],
  );

  const handleModelChange = useCallback(() => {
    const designs = form.getFieldValue('designs');
    if (designs.length > 0) {
      designs[currentDesignIdx].sizeId = phoneModel;
      form.setFieldsValue({ designs: designs });
    }
  }, [form, currentDesignIdx, phoneModel]);

  const getResendRequestBtnColor = () => {
    switch (order?.resendRequest?.status) {
      case RequestStatus.PENDING:
        return '#1d39c4';
      case RequestStatus.APPROVED:
        return '#389e0d';
      case RequestStatus.REJECTED:
        return '#dc143c';
    }
  };

  const handlePickDesign = (idx: number) => {
    setCurrentDesignIdx(idx);
    setVisibleModal(true);
  };

  const handleDesignPhone = (idx: number) => {
    setCurrentDesignIdx(idx);
    setShowPhoneDesignCanvas(true);
  };

  const getHeaderContent = () => {
    if (isEditing) {
      return ` ${OrderDetailTitleMap[productType]} Order Detail`;
    }

    return `Create ${OrderDetailTitleMap[productType]} Order`;
  };

  useEffect(() => {
    fetchSize();
    if (!!id) {
      fetchOrder(id);
    }
  }, [id, fetchSize, fetchOrder]);

  useEffect(() => {
    if (!isPhoneCaseProduct) {
      setOrder((prevOrder) => ({
        ...prevOrder,
        designs: [
          {
            quantity: 1,
            itemCost: 0,
          },
        ],
      }));
      form.setFieldValue('designs', [
        {
          quantity: 1,
          itemCost: 0,
        },
      ]);
    }
  }, [isPhoneCaseProduct, form]);

  // Products with a single size (e.g. Ornament's "default") preselect it so
  // the seller only has to pick a variant. Must run before the total effect
  // below, which re-prices the rows once sizes are loaded.
  useEffect(() => {
    if (!onlySizeId) return;
    const designs: IOrderItem[] = form.getFieldValue('designs') ?? [];
    if (designs.some((d) => !d?.sizeId)) {
      form.setFieldValue(
        'designs',
        designs.map((d) => (d?.sizeId ? d : { ...d, sizeId: onlySizeId })),
      );
    }
  }, [onlySizeId, form]);

  useEffect(() => {
    if (!!phoneModel) {
      handleModelChange();
    }
    handleTotal({}, form.getFieldsValue());
  }, [phoneModel, listDesign, handleModelChange, handleTotal, form]);

  return (
    <>
      {loading && (
        <SpinContainer>
          <Spin size="large" />
        </SpinContainer>
      )}
      <PageContainer>
        <RequestResendModal
          visible={visibleRequestModal}
          onCancel={() => setVisibleRequestModal(false)}
          order={order}
          onConfirm={(request) =>
            setOrder((order) => ({ ...order, resendRequest: request }))
          }
          onApprove={(request) => {
            fetchOrder(id || '');
          }}
          onReject={(request) => {
            setOrder((order) => ({ ...order, resendRequest: request }));
          }}
          onCreateNew={(request) => {
            setVisibleRequestModal(false);
            setOrder((order) => ({ ...order, resendRequest: undefined }));
            setVisibleRequestModal(true);
          }}
        />
        <PageHeader
          title={getHeaderContent()}
          showBackButton
          addButton={
            <ButtonGroupContainer>
              {!!id && (
                <Button
                  htmlType="button"
                  type={ButtonType.OUTLINE}
                  onClick={() => navigate(`/order/${id}/invoice`)}
                >
                  <FileTextOutlined size={16} />
                  <span className="font-size--14">View Invoice</span>
                </Button>
              )}
              {!!order.resendRequest ||
              (currentUser?.role === Role.SELLER &&
                order.status === OrderStatus.SHIPPED) ? (
                <Button
                  onClick={() => {
                    setVisibleRequestModal(true);
                  }}
                  color={getResendRequestBtnColor()}
                >
                  Resend Request{' '}
                  {!!order.resendRequest
                    ? `(${order.resendRequest?.status})`
                    : null}
                </Button>
              ) : null}
              {!!id && status === OrderStatus.PENDING && (
                <Button
                  disabled={loading}
                  htmlType="button"
                  onClick={deleteOrder}
                  type={ButtonType.DANGER}
                >
                  <DeleteOutlined size={16} />
                  <span className="font-size--14">Delete</span>
                </Button>
              )}
              {!!id &&
                isStickerOrUVDTFProduct &&
                order?.shippingLabelUrl &&
                isAdmin && (
                  <Button
                    disabled={loading}
                    htmlType="button"
                    onClick={() =>
                      window.open(order.shippingLabelUrl, '_blank')
                    }
                  >
                    <span className="font-size--14">View Label</span>
                  </Button>
                )}
              {isAdmin &&
                !!id &&
                !order.trackingReference &&
                !order.shippingLabelUrl &&
                // Ornament ships in a box via Shippo; EasyPost is Letter-only.
                productType !== ProductType.ornament && (
                  <Button
                    disabled={loading}
                    type={ButtonType.OUTLINE}
                    htmlType="button"
                    onClick={async () => {
                      if (!order.address) {
                        message.error(
                          'Order address is required for purchasing shipment',
                        );
                        return;
                      }

                      try {
                        setLoading(true);
                        const response = await orderApi.purchaseShipment(
                          id,
                          order.address,
                        );
                        message.success('Shipment purchased successfully!');

                        form.setFieldValue(
                          'trackingReference',
                          response.trackingReference,
                        );

                        // Update order data with new shipping information
                        setOrder({
                          ...order,
                          shippingLabelUrl: response.shippingLabelUrl,
                          trackingReference: response.trackingReference,
                          trackingUrl: response.trackingUrl,
                          shippingCarrier: response.shippingCarrier,
                          shippingService: response.shippingService,
                          estimatedDeliveryDate: response.estimatedDeliveryDate,
                          shippingCost: response.shippingCost,
                          shipmentId: response.shipmentId,
                          shipmentStatus: response.shipmentStatus,
                        });
                      } catch (e) {
                        message.error('Failed to purchase shipment');
                        console.error(e);
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    <ShoppingCartOutlined size={16} />
                    <span className="font-size--14">Buy Shipment</span>
                  </Button>
                )}
              {isAdmin &&
                !!id &&
                isNeedsShippoPurchase(
                  order.trackingReference,
                  order.shippingLabelUrl,
                  order.shipmentId,
                ) &&
                isShippoEligibleOrderType(ProductOrderTypeMap[productType]) &&
                country === Country.US &&
                (status === OrderStatus.PENDING ||
                  status === OrderStatus.PROCESSING) && (
                  <Button
                    disabled={loading}
                    type={ButtonType.OUTLINE}
                    htmlType="button"
                    onClick={async () => {
                      if (!order.address) {
                        message.error(
                          'Order address is required for purchasing Shippo label',
                        );
                        return;
                      }

                      try {
                        setLoading(true);
                        const response = await orderApi.purchaseShippoShipment(
                          id,
                          order.address,
                        );
                        message.success('Shippo label purchased successfully!');

                        form.setFieldValue(
                          'trackingReference',
                          response.trackingReference,
                        );

                        setOrder({
                          ...order,
                          shippingLabelUrl: response.shippingLabelUrl,
                          trackingReference: response.trackingReference,
                          trackingUrl: response.trackingUrl,
                          shippingCarrier: response.shippingCarrier,
                          shippingService: response.shippingService,
                          estimatedDeliveryDate: response.estimatedDeliveryDate,
                          shippingCost: response.shippingCost,
                          labelFee: response.labelFee,
                          labelProvider: response.labelProvider,
                          shipmentId: response.shipmentId,
                          shipmentStatus: response.shipmentStatus,
                          status: response.status || order.status,
                          totalCost: response.totalCost,
                          shouldLockTotalCost: response.shouldLockTotalCost,
                        });
                        if (response.totalCost != null) {
                          setTotal(response.totalCost);
                          setShouldLockTotalCost(
                            !!response.shouldLockTotalCost,
                          );
                        }
                      } catch (e: any) {
                        message.error(
                          e?.response?.data?.message ||
                            'Failed to purchase Shippo label',
                        );
                        console.error(e);
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    <ShoppingCartOutlined size={16} />
                    <span className="font-size--14">Buy Shippo Label</span>
                  </Button>
                )}
              {isAdmin &&
                !!id &&
                isShippoEligibleOrderType(ProductOrderTypeMap[productType]) &&
                country === Country.US &&
                !order.shipmentId &&
                !!order.trackingReference &&
                !(NeedsShippoPurchaseSentinels as readonly string[]).includes(
                  order.trackingReference,
                ) && (
                  <Button
                    disabled={loading}
                    type={ButtonType.OUTLINE}
                    htmlType="button"
                    onClick={async () => {
                      if (!id) return;
                      try {
                        setLoading(true);
                        await shippoApi.syncOrder(id);
                        message.success(
                          'Shippo label linked to this order',
                        );
                        await fetchOrder(id);
                      } catch (e: any) {
                        message.error(
                          e?.response?.data?.message ||
                            'Failed to sync with Shippo',
                        );
                        console.error(e);
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    <SyncOutlined size={16} />
                    <span className="font-size--14">Sync Shippo</span>
                  </Button>
                )}
            </ButtonGroupContainer>
          }
        />

        <OrderContentWrapper>
          <OrderFormSection>
            <CustomForm
              form={form}
              initialValues={order}
              layout="vertical"
              requiredMark="optional"
              labelAlign="left"
              onFinish={() => {
                // Show confirmation modal instead of submitting directly
                setConfirmationModalVisible(true);
              }}
              disabled={isFormDisabled}
              onValuesChange={handleTotal}
            >
              <OrderInformationSection>
                <h3>Order Information</h3>

                <OrderInfoRow className={!!id && isAdmin ? '' : 'full-width'}>
                  <OrderInfoField>
                    <Form.Item
                      name="orderId"
                      label="Order ID"
                      rules={[
                        {
                          required: true,
                          type: 'string',
                          message: 'Please order id',
                        },
                      ]}
                    >
                      <CustomInput
                        placeholder="Order ID"
                        onChange={(e) => {
                          setSellerOrderId(e.target.value);
                        }}
                      />
                    </Form.Item>
                  </OrderInfoField>

                  <OrderInfoField>
                    {!!id && isAdmin && (
                      <Form.Item name="status" label="Status">
                        <StyledSelect
                          className="w-100"
                          value={status}
                          onChange={(newValue: any) => {
                            form.setFieldValue('status', newValue);
                          }}
                          options={[
                            { label: 'Pending', value: OrderStatus.PENDING },
                            { label: 'Process', value: OrderStatus.PROCESSING },
                            { label: 'Shipped', value: OrderStatus.SHIPPED },
                            {
                              label: 'ReShipped',
                              value: OrderStatus.RESHIPPED,
                            },
                            { label: 'Cancel', value: OrderStatus.CANCELLED },
                          ]}
                        />
                      </Form.Item>
                    )}
                  </OrderInfoField>
                </OrderInfoRow>

                <OrderInfoRow className="full-width">
                  <OrderInfoField>
                    <Form.Item
                      name="address"
                      label="Address"
                      rules={[
                        {
                          required: true,
                          type: 'string',
                          message: 'Please input address',
                        },
                      ]}
                      help={
                        isStickerOrUVDTFProduct ? (
                          <div className="m__t--8">
                            Example format:
                            <br />
                            DANIELLE LURIE
                            <br />
                            2222 WILLOW GLEN DRIVE NW
                            <br />
                            DOVER OH 44622
                          </div>
                        ) : undefined
                      }
                    >
                      <CustomTextArea
                        autoSize={{ minRows: 3, maxRows: 4 }}
                        placeholder="Please enter recipient's full address"
                        maxLength={1000}
                      />
                    </Form.Item>
                  </OrderInfoField>
                </OrderInfoRow>

                <OrderInfoRow className="full-width">
                  <OrderInfoField>
                    <Form.Item name="notes" label="Notes">
                      <CustomTextArea
                        autoSize={{ minRows: 3, maxRows: 4 }}
                        placeholder="Enter notes"
                        maxLength={1000}
                      />
                    </Form.Item>
                  </OrderInfoField>
                </OrderInfoRow>

                <OrderInfoRow>
                  <OrderInfoField>
                    {(!id || (!!id && isAdmin)) && (
                      <>
                        <Form.Item
                          name="trackingReference"
                          label="Tracking"
                          rules={
                            trackingRequired
                              ? [
                                  {
                                    required: true,
                                    whitespace: true,
                                    message: `Tracking reference is required for ${OrderDetailTitleMap[productType]}.`,
                                  },
                                ]
                              : undefined
                          }
                          required={trackingRequired}
                        >
                          <CustomInput
                            placeholder={
                              trackingRequired
                                ? 'Tracking (required)'
                                : 'Tracking'
                            }
                            onChange={(e) => {
                              // For sticker products, auto-set createShippingLabel based on tracking reference
                              if (!isPhoneCaseProduct) {
                                const trackingValue = e.target.value;
                                if (isStickerOrUVDTFProduct) {
                                  form.setFieldsValue({
                                    createShippingLabel:
                                      !trackingValue ||
                                      trackingValue.trim() === '',
                                  });
                                }
                                setHasOwnTrackingReference(
                                  !!trackingValue &&
                                    trackingValue.trim() !== '',
                                );
                              }
                            }}
                            disabled={disableTrackingReference}
                          />
                        </Form.Item>
                        {!isAdmin && (
                          <Checkbox
                            className="m__t--8"
                            onChange={(e) => {
                              if (e.target.checked) {
                                setDisableTrackingReference(e.target.checked);
                                form.setFieldValue(
                                  'trackingReference',
                                  RequestAdminPurchaseShipment,
                                );
                                setHasOwnTrackingReference(true);
                              } else {
                                setDisableTrackingReference(false);
                                form.setFieldValue('trackingReference', '');
                                setHasOwnTrackingReference(false);
                              }
                            }}
                          >
                            Cần Tracking
                          </Checkbox>
                        )}
                      </>
                    )}
                  </OrderInfoField>
                </OrderInfoRow>
                <OrderInfoRow>
                  <OrderInfoField>
                    <Form.Item name="country" label="Country">
                      <StyledSelect
                        className="w-100"
                        placeholder="Choose country"
                        value={country}
                        onChange={(newValue: any) => {
                          form.setFieldValue('country', newValue);
                          setCountry(newValue);
                        }}
                        options={[
                          { label: 'US', value: Country.US },
                          { label: 'CANADA', value: Country.CANADA },
                        ]}
                      />
                    </Form.Item>
                  </OrderInfoField>
                </OrderInfoRow>
                {showHardTubeOption && (
                  <OrderInfoField>
                    <Form.Item
                      name="shipWithHardTube"
                      valuePropName="checked"
                      label=" "
                    >
                      <Checkbox>SHIP WITH HARD TUBE</Checkbox>
                    </Form.Item>
                  </OrderInfoField>
                )}
              </OrderInformationSection>
              <Form.List name="designs">
                {(fields, { add, remove }) => (
                  <OrderItemsContainer>
                    <h3>
                      Product{fields.length > 1 ? 's' : ''} ({fields.length})
                    </h3>

                    {fields.map(({ key, name, ...restField }) => (
                      <div>
                        <OrderItemWrapper key={name}>
                          <ImageContainer
                            $active={
                              !showPhoneDesignCanvas &&
                              !isPhoneCaseProduct &&
                              !listDesign[name]
                            }
                            onClick={
                              showPhoneDesignCanvas ||
                              (isPhoneCaseProduct && !!listDesign[name])
                                ? undefined
                                : isPhoneCaseProduct
                                  ? () => {
                                      handleDesignPhone(name);
                                    }
                                  : () => {
                                      handlePickDesign(name);
                                    }
                            }
                          >
                            {!!listDesign[name] ? (
                              <img
                                src={listDesign[name]}
                                alt="Design preview"
                              />
                            ) : (
                              <PlusOutlined style={{ fontSize: 24 }} />
                            )}
                          </ImageContainer>
                          <CreateOrderItemWrapper $hasVariant={hasVariants}>
                            <Form.Item hidden name="id" label="Id">
                              <CustomInput placeholder="Id" />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, 'sizeId']}
                              rules={[{ required: true, message: '' }]}
                              label={isPhoneCaseProduct ? 'Model' : 'Size'}
                              help={
                                !isPhoneCaseProduct && sizes.length === 0
                                  ? `No ${OrderDetailTitleMap[productType]} sizes have been added yet. Please contact an admin.`
                                  : undefined
                              }
                            >
                              <StyledSelect
                                className="w-100"
                                placeholder={
                                  isPhoneCaseProduct
                                    ? 'Phone Model'
                                    : 'Pick a size'
                                }
                                options={sizes?.map((s) => ({
                                  // Out-of-stock sizes stay visible so the
                                  // seller can see the catalogue, but are
                                  // greyed out and not pickable. The "(out
                                  // of stock)" suffix is the reason.
                                  label: s.isOutOfStock
                                    ? `${s.size} (out of stock)`
                                    : s.size,
                                  value: s.id,
                                  disabled: !!s.isOutOfStock,
                                }))}
                                disabled={isPhoneCaseProduct}
                                value={phoneModel}
                              />
                            </Form.Item>
                            {hasVariants && (
                              <Form.Item
                                noStyle
                                shouldUpdate={(prev, cur) =>
                                  prev.designs?.[name]?.sizeId !==
                                  cur.designs?.[name]?.sizeId
                                }
                              >
                                {() => {
                                  const sizeId = form.getFieldValue([
                                    'designs',
                                    name,
                                    'sizeId',
                                  ]);
                                  return (
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'variant']}
                                      rules={[
                                        {
                                          required: true,
                                          message: 'Pick a variant',
                                        },
                                      ]}
                                      label="Variant"
                                    >
                                      <StyledSelect
                                        className="w-100"
                                        placeholder="Pick a variant"
                                        options={getRowVariantOptions(sizeId)}
                                      />
                                    </Form.Item>
                                  );
                                }}
                              </Form.Item>
                            )}
                            <Form.Item
                              {...restField}
                              name={[name, 'quantity']}
                              rules={[
                                {
                                  required: true,
                                  message: '',
                                },
                              ]}
                              label="Quantity"
                            >
                              <StyledInputNumber
                                min={1}
                                max={1000000}
                                placeholder="Quantity"
                                step={1}
                                className="w-100"
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, 'itemCost']}
                              label="Cost"
                            >
                              <StyledInputNumber className="w-100" disabled />
                            </Form.Item>
                            <DeleteIconContainer>
                              <DeleteIcon
                                onClick={() => {
                                  remove(name);
                                  const tmpDesign = cloneDeep(listDesign);
                                  tmpDesign.splice(name, 1);
                                  setListDesign(tmpDesign);
                                  if (currentDesignIdx === name) {
                                    setCurrentDesignIdx(0);

                                    if (showPhoneDesignCanvas === true) {
                                      setShowPhoneDesignCanvas(false);
                                    }
                                  } else if (name < currentDesignIdx) {
                                    setCurrentDesignIdx(currentDesignIdx - 1);
                                  }
                                  setPhoneModel(undefined);
                                }}
                              />
                            </DeleteIconContainer>
                          </CreateOrderItemWrapper>
                        </OrderItemWrapper>
                        {showPhoneDesignCanvas && currentDesignIdx === name && (
                          <PhoneCaseEditorContainer>
                            <PhoneCaseEditor
                              onChangePhoneModel={(model) => {
                                const size = sizes.find(
                                  (s) => s.size === model,
                                );
                                if (!!size) {
                                  setPhoneModel(size.id);
                                }
                              }}
                              orderId={sellerOrderId ?? ''}
                              onDesignSubmitted={(design) => {
                                const currentFormValue: IOrderItem[] =
                                  form.getFieldValue('designs');
                                currentFormValue[currentDesignIdx] = {
                                  ...currentFormValue[currentDesignIdx],
                                  designId: design.id || '',
                                };
                                form.setFieldValue('designs', currentFormValue);
                                const tmp = cloneDeep(listDesign);
                                if (!design.originalFileUrl) {
                                  return;
                                }

                                tmp[currentDesignIdx] = design.originalFileUrl;
                                setListDesign(tmp);
                                setShowPhoneDesignCanvas(false);
                                setPhoneModel(undefined);
                              }}
                            />
                          </PhoneCaseEditorContainer>
                        )}
                      </div>
                    ))}
                    <Form.Item>
                      <AddItemButton
                        className="w-100"
                        // type="dashed"
                        onClick={() => {
                          add({
                            quantity: 1,
                            itemCost: 0,
                            sizeId: onlySizeId,
                          });
                          const tmpDesign = cloneDeep(listDesign);
                          tmpDesign.push('');
                          setListDesign(tmpDesign);
                        }}
                        // block
                        // icon={<PlusOutlined />}
                        disabled={
                          isPhoneCaseProduct &&
                          (!sellerOrderId ||
                            sellerOrderId?.length === 0 ||
                            showPhoneDesignCanvas)
                        }
                        type={ButtonType.OUTLINE}
                      >
                        <PlusOutlined /> Add item{' '}
                        {isPhoneCaseProduct &&
                          '- You must input order id to start designing phone case'}
                      </AddItemButton>
                    </Form.Item>
                  </OrderItemsContainer>
                )}
              </Form.List>

              {!!id && isStickerOrUVDTFProduct && (
                <ShippingInformationSection className="m__t--24">
                  <h3>Shipping Information</h3>

                  <ShippingInfoRow>
                    <ShippingInfoField>
                      <label>Shipping Carrier</label>
                      <div className="value">
                        {order?.shippingCarrier || '-'}
                      </div>
                    </ShippingInfoField>

                    <ShippingInfoField>
                      <label>Service</label>
                      <div className="value">
                        {order?.shippingService || '-'}
                      </div>
                    </ShippingInfoField>

                    <ShippingInfoField>
                      <label>Est. Delivery</label>
                      <div className="value">
                        {order?.estimatedDeliveryDate
                          ? dayjs(order.estimatedDeliveryDate).format(
                              'DD MMM YYYY',
                            )
                          : '-'}
                      </div>
                    </ShippingInfoField>
                  </ShippingInfoRow>

                  <ShippingInfoRow>
                    <ShippingInfoField>
                      <label>Status</label>
                      <div className="value">
                        {order?.shipmentStatus || '-'}
                      </div>
                    </ShippingInfoField>

                    <ShippingInfoField>
                      <label>Shipment ID</label>
                      <div className="value">
                        {isAdmin ? order?.shipmentId || '-' : '-'}
                      </div>
                    </ShippingInfoField>

                    <ShippingInfoField>
                      <label>Tracking</label>
                      <div className="value">
                        {order?.trackingUrl ? (
                          <a
                            href={order.trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {order.trackingReference}
                          </a>
                        ) : (
                          order?.trackingReference || '-'
                        )}
                      </div>
                    </ShippingInfoField>
                  </ShippingInfoRow>
                </ShippingInformationSection>
              )}
            </CustomForm>
          </OrderFormSection>

          <CheckoutDraftContainer>
            <h3>Order Summary</h3>

            <CheckoutDraftContent>
              {!isPhoneCaseProduct ? (
                showsSellerShippingFee() ? (
                  <CheckoutDraftRow>
                    <p>
                      {(
                        form.getFieldValue('country') || Country.US
                      )?.toUpperCase()}{' '}
                      shipping fee
                    </p>
                    <p>${getSellerShippingFee().toFixed(2)}</p>
                  </CheckoutDraftRow>
                ) : null
              ) : (
                <CheckoutDraftRow>
                  <p>Additional fee per design</p>
                  <p>${totalPhoneCaseCount - 1}</p>
                </CheckoutDraftRow>
              )}
              {showHardTubeOption &&
                form.getFieldValue('shipWithHardTube') && (
                  <CheckoutDraftRow>
                    <p>Hard tube shipping</p>
                    <p>${ShipWithHardTubeExtraCharge}</p>
                  </CheckoutDraftRow>
                )}
              {!!order?.labelFee && (
                <CheckoutDraftRow>
                  <p>Shippo label fee</p>
                  <p>${Number(order.labelFee).toFixed(2)}</p>
                </CheckoutDraftRow>
              )}
              <CheckoutDraftRow>
                <p>Total</p>
                <p>${total.toLocaleString('en-US')}</p>
              </CheckoutDraftRow>
            </CheckoutDraftContent>

            <ButtonGroupContainer className="m__t--24">
              <Button
                htmlType="submit"
                disabled={
                  !listDesign.length ||
                  (!!id && order?.status !== OrderStatus.PENDING && !isAdmin)
                }
                onClick={() => {
                  // Show confirmation modal instead of submitting directly
                  setConfirmationModalVisible(true);
                }}
              >
                <CheckOutlined size={16} />
                Save
              </Button>
            </ButtonGroupContainer>
          </CheckoutDraftContainer>
        </OrderContentWrapper>
      </PageContainer>
      <SearchDesignModal
        visible={visibleModal}
        sizes={sizes}
        onCancel={() => setVisibleModal(false)}
        onSelect={(design: IDesign) => {
          const currentFormValue: IOrderItem[] = form.getFieldValue('designs');
          currentFormValue[currentDesignIdx] = {
            ...currentFormValue[currentDesignIdx],
            designId: design.id || '',
          };
          form.setFieldValue('designs', currentFormValue);
          const tmp = cloneDeep(listDesign);
          if (!design.designUrl) {
            return;
          }

          tmp[currentDesignIdx] = design.thumbnailUrl ?? design.designUrl;
          setListDesign(tmp);
        }}
        product={productType}
      />

      {/* Order Confirmation Modal */}
      <ConfirmModal
        visible={confirmationModalVisible}
        onOk={() => {
          setConfirmationModalVisible(false);
          // Process the form submission with the saved values
          if (!!id) {
            updateOrder();
          } else {
            createOrder();
          }
        }}
        onCancel={() => {
          setConfirmationModalVisible(false);
        }}
        title="Confirm Order"
        okText="Confirm"
        cancelText="Cancel"
      >
        <p>Are you sure you want to {!!id ? 'update' : 'submit'} this order?</p>
        <p>Please check all details before confirming.</p>
      </ConfirmModal>
    </>
  );
};
export default OrderDetail;
